import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import Fab from '@mui/material/Fab';
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import styled from 'styled-components';
import HeaderInfoM from '../lib/typography/HeaderInfoM';

function isValid(value) {
  return value.length >= 0 && value.length < 255;
}

const Wrapper = styled.div`
  display: flex;
  justifyContent: center;
  flex-direction: column;
  padding: 15px;
  border 1px solid #CCC;
  border-radius: 6px;
`;

const capitalize = (s) => {
  if (typeof s !== 'string') return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const nutritionFieldsOrdered = [
  'ENERGY VALUE',
  'FATS',
  'SATURATED FATS',
  'CARBOHYDRATES',
  'SUGARS',
  'FIBERS',
  'PROTEINS',
  'SALTS',
];

const nutritionFieldsName = {
  'ENERGY VALUE': 'Energy Value',
  FATS: 'Fats',
  'SATURATED FATS': 'Saturated Fats',
  CARBOHYDRATES: 'Carbohydrates',
  SUGARS: 'Sugars',
  FIBERS: 'Fibers',
  PROTEINS: 'Proteins',
  SALTS: 'Salts',
};

const nutritionFieldsUnits = {
  'ENERGY VALUE': 'KCal',
  FATS: 'g',
  'SATURATED FATS': 'g',
  CARBOHYDRATES: 'g',
  SUGARS: 'g',
  FIBERS: 'g',
  PROTEINS: 'g',
  SALTS: 'g',
};

export const EditNutritionTags = ({ product, type, setFieldValue }) => {
  const [nutrition, setNutrition] = useState(
    product[type] || {
      totalQty: 0,
      portion: {},
      '100g': {},
      additives: null,
      others: null,
    },
  );

  useEffect(() => {
    setFieldValue(type, nutrition);
  }, [nutrition]);

  const handleChangeNutritionQty = (e) => {
    const regex = /^[0-9\b]+$/;
    const value = regex.test(e.target.value)
      ? Number(e.target.value)
      : e.target.value.length === 0
      ? ''
      : null;

    if (value === null || value > 10000000) {
      return;
    }

    const newNutrition = {
      ...nutrition,
      totalQty: value,
    };

    if (value === '') {
      newNutrition.totalQty = 0;
    }

    const changeInPortionQty = nutrition.totalQty !== newNutrition.totalQty;

    if (changeInPortionQty) {
      newNutrition.portion = {};
    }

    setNutrition(newNutrition);
  };

  const calculateOtherField = (data) => {
    // const data = {
    //   totalQty: 400,
    //   portion: null | number,
    //   '100g': null | number,
    // };

    if (data.totalQty === 0) {
      return 0;
    }

    if (data.portion && !data['100g']) {
      return Number(parseFloat(String((data.portion * 100) / data.totalQty)).toFixed(2));
    }

    if (data['100g'] && !data.portion) {
      return Number(parseFloat(String((data['100g'] * data.totalQty) / 100))).toFixed(2);
    }

    return 0;
  };

  const handleChangeNutritionType = (e, nutritionType, qtyField) => {
    let incomingValue = typeof e.target.value !== 'string' ? '' : e.target.value.trim();
    const isEmptyString = incomingValue !== '';
    const hasDot = incomingValue.length > 1 && incomingValue[incomingValue.length - 1] !== '.';

    if (incomingValue.length > 0 && isNaN(incomingValue[incomingValue.length - 1])) {
      if (incomingValue[incomingValue.length - 1] !== '.') {
        incomingValue = incomingValue.slice(0, -1);
      }
    }

    if (isNaN(incomingValue)) {
      if (!isEmptyString) {
        return;
      }
    }

    let value = isEmptyString
      ? incomingValue
      : !isNaN(incomingValue)
      ? Number(parseFloat(String(incomingValue)).toFixed(2))
      : '';

    if (isNaN(value)) {
      value = '';
    }

    if (String(value).length > 10) {
      return;
    }

    const otherQtyField = qtyField === '100g' ? 'portion' : '100g';

    const newQty = {};
    newQty[qtyField] = { ...nutrition[qtyField] };
    newQty[qtyField][nutritionType] = value;
    const newNutrition = {
      ...nutrition,
      ...newQty,
    };

    if (value === '') {
      delete newNutrition[qtyField][nutritionType];
    }

    const data = { totalQty: newNutrition.totalQty };
    data[qtyField] = value;
    data[otherQtyField] = null;

    const otherCalculatedValue = calculateOtherField(data);
    const otherFieldHasFalseInfo = newNutrition?.[otherQtyField]?.[nutritionType]
      ? Math.abs(newNutrition[otherQtyField][nutritionType] - otherCalculatedValue) > 0
      : true;

    if (otherFieldHasFalseInfo) {
      if (otherCalculatedValue !== 0) {
        newNutrition[otherQtyField][nutritionType] = otherCalculatedValue;
      }
    }

    setNutrition(newNutrition);
  };

  const handleChangeAdditives = (e) => {
    if (e.target.value.length > 512) {
      return;
    }

    const newNutrition = {
      ...nutrition,
      additives: e.target.value,
    };

    setNutrition(newNutrition);
  };

  const handleChangeOthers = (e) => {
    if (e.target.value.length > 512) {
      return;
    }

    const newNutrition = {
      ...nutrition,
      others: e.target.value,
    };

    setNutrition(newNutrition);
  };

  const info = (
    <p className="f1 f08 mb-16 ml-2 txt-info" style={{ marginTop: -10 }}>
      Only the fieds that have values are shown on the menu
    </p>
  );

  return (
    <Wrapper>
      <div className="f-c f-aic">
        <div className="f-1">
          <HeaderInfoM
            name={`${type === 'nutrition' ? 'NUTRITION / PORTION' : type.toUpperCase()}`}
            size="1rem"
            color="#000000"
            style={{ padding: '2px', letterSpacing: '-0.05em', marginBottom: '10px' }}
          />
        </div>
        <div className="f-c f-1 f-aic">
          <div className="f-1">
            <TextField
              size="small"
              id={`edit-portion-qty-${type}`}
              label={`Size in grams`}
              variant="outlined"
              value={nutrition.totalQty}
              onChange={handleChangeNutritionQty}
              style={{ padding: '0 0 0 4px', width: 100 }}
            />
          </div>
          {nutrition.totalQty === 0 && (
            <p className="f07 txt-danger ml-4">Please set the portion size first</p>
          )}
        </div>
      </div>
      {info}
      <Grid container>
        {nutritionFieldsOrdered.map((nutritionType, index) => {
          const labelUnits = nutritionFieldsUnits[nutritionType];
          const labelUnitsText = labelUnits ? `(${labelUnits}) / ${nutrition.totalQty ?? 0}g` : '';

          return (
            <Grid key={index} item xs={6}>
              <div className="f-c mb-8">
                <TextField
                  fullWidth
                  disabled={nutrition.totalQty === 0}
                  size="small"
                  id={`edit-${type}-portion-${nutritionType}-${index}`}
                  label={`${nutritionFieldsName[nutritionType]} ${labelUnitsText}`}
                  variant="outlined"
                  value={nutrition?.['portion']?.[nutritionType] ?? ''}
                  onChange={(e) => handleChangeNutritionType(e, nutritionType, 'portion')}
                  style={{ padding: '0 4px 0 4px' }}
                />
              </div>
            </Grid>
          );
        })}
      </Grid>
      <HeaderInfoM
        name={`${type === 'nutrition' ? 'NUTRITION / 100g' : type.toUpperCase()}`}
        size="1rem"
        color="#000000"
        style={{
          padding: '2px',
          letterSpacing: '-0.05em',
          marginBottom: '10px',
          marginTop: '10px',
        }}
      />
      {info}
      <Grid container>
        {nutritionFieldsOrdered.map((nutritionType, index) => {
          const labelUnits = nutritionFieldsUnits[nutritionType];
          const labelUnitsText = labelUnits ? `(${labelUnits}) / 100g` : '';

          return (
            <Grid key={index} item xs={6}>
              <div className="f-c mb-8">
                <TextField
                  fullWidth
                  disabled={nutrition.totalQty === 0}
                  size="small"
                  id={`edit-${type}-100g-${nutritionType}-${index}`}
                  label={`${nutritionFieldsName[nutritionType]} ${labelUnitsText}`}
                  variant="outlined"
                  value={nutrition?.['100g']?.[nutritionType] ?? ''}
                  onChange={(e) => handleChangeNutritionType(e, nutritionType, '100g')}
                  style={{ padding: '0 4px 0 4px' }}
                />
              </div>
            </Grid>
          );
        })}
      </Grid>
      <HeaderInfoM
        name={`${type === 'nutrition' ? 'EXTRA' : type.toUpperCase()}`}
        size="1rem"
        color="#000000"
        style={{
          padding: '2px',
          letterSpacing: '-0.05em',
          marginBottom: '10px',
          marginTop: '10px',
        }}
      />
      <Grid container>
        <Grid item xs={12}>
          <div className="f-c mb-8">
            <TextField
              fullWidth
              size="small"
              id={`edit-${type}-additives`}
              label="Additives"
              variant="outlined"
              value={nutrition?.additives}
              onChange={(e) => handleChangeAdditives(e)}
              style={{ padding: '0 4px 0 4px' }}
            />
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className="f-c mb-8">
            <TextField
              fullWidth
              size="small"
              id={`edit-${type}-others`}
              label="Others"
              variant="outlined"
              value={nutrition?.others}
              onChange={(e) => handleChangeOthers(e)}
              style={{ padding: '0 4px 0 4px' }}
            />
          </div>
        </Grid>
      </Grid>
    </Wrapper>
  );
};
